import { RouteNavigationAction, UrlType } from '@varo/graphql-schema-types';
import rewardFeaturedCardTextureBgDefault from 'assets/images/vxl/textures/reward-featured-card-texture-bg-default.png';
import { Href, useRouter } from 'expo-router';
import OfferDetailsHeader from 'features/rewards/Details/OfferDetailsHeader/OfferDetailsHeader';
import useIncentiveDetails from 'features/rewards/hooks/useIncentiveDetails';
import React from 'react';
import {
  ImageSourcePropType,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import StepCheckListTracker from 'shared/components/StepCheckListTracker/StepCheckListTracker';
import Button from 'shared/components/vxl/Button/Button';
import Icon, { IconKeyType } from 'shared/components/vxl/icon/Icon/Icon';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import { NavigationBar } from 'shared/components/vxl/navigation/NavigationBar/NavigationBar';
import { StepChecklistProps } from 'shared/components/vxl/ProgressIndicator/StepChecklist/StepChecklist';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { isIOS } from 'shared/constants/platform';
import { useViewDidFocusLogScreenAnalytics } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

import MultiStepTag from './MultiStepTag/MultiStepTag';
import MultiStepTermsSection from './MultiStepTermsSection/MultiStepTermsSection';

const Divider = () => <View style={styles.dividerStyle} />;

interface MultiStepIncentiveDialogProps {
  id: string;
  ErrorComponent: React.ReactNode;
}

const MultiStepIncentiveDialog = ({ id }: MultiStepIncentiveDialogProps) => {
  const router = useRouter();
  const {
    incentiveDetails,
    loading,
    error,
    intervalTrackerData,
    stepCheckListData,
  } = useIncentiveDetails(id);
  const { bottom } = useSafeAreaInsets();
  const { defaultTitle, defaultMessage, cancelButtonTitle } =
    useDefaultErrorStrings();

  useViewDidFocusLogScreenAnalytics({
    name: 'Incentive Offer',
    properties: {
      incentive_name: incentiveDetails?.incentiveTitle,
      incentive_variant: incentiveDetails?.ctaButton?.buttonText ?? '',
      offer_id: incentiveDetails?.id,
      category: 'incentive',
    },
  });

  const handlePdfClick = (pdfUrl: string) => {
    if (pdfUrl) {
      router.push({
        pathname: '/rewards/disclosure',
        params: {
          pdfUrl,
        },
      });
    }
  };

  return (
    <View style={[styles.mainContainer, { paddingBottom: bottom }]}>
      <NavigationBar
        title=""
        primaryIcon={<Icon iconKey="CLOSE" iconSize="MD" />}
        primaryOnPress={() => router.back()}
      />

      <ScreenTransitionOverlay
        isLoading={loading}
        LoadingComponent={
          <View style={styles.loader}>
            <LoadingSpinner />
          </View>
        }
        ErrorComponent={
          error ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={defaultTitle}
              messageBody={defaultMessage}
              primaryButton={{
                title: cancelButtonTitle,
                onPress: () => router.back(),
              }}
            />
          ) : null
        }
      />

      <ScrollView>
        <View style={styles.container}>
          <View style={styles.offerDetailsHeaderContainer}>
            <OfferDetailsHeader
              defaultHeroImage={
                rewardFeaturedCardTextureBgDefault as ImageSourcePropType
              }
              heroImage={(incentiveDetails?.heroImage as UrlType)?.url}
              logoImage={(incentiveDetails?.centerImage as UrlType)?.url}
            />
          </View>

          <View style={styles.headerContainer}>
            {incentiveDetails?.headerTag?.text ? (
              <MultiStepTag
                text={incentiveDetails.headerTag.text}
                tagColor={incentiveDetails.headerTag.tagColor}
                icon={incentiveDetails.headerTag.icon}
              />
            ) : null}

            {incentiveDetails?.incentiveTitle ? (
              <TitleLgText style={styles.bodyTextContainer}>
                {incentiveDetails.incentiveTitle}
              </TitleLgText>
            ) : null}

            {incentiveDetails?.incentiveSubTitle ? (
              <BodyBaseText style={styles.bodyTextContainer}>
                {incentiveDetails.incentiveSubTitle}
              </BodyBaseText>
            ) : null}
          </View>

          <Divider />

          {incentiveDetails?.alert?.title ? (
            <View style={styles.alertContainer}>
              <InlineAlert
                titleText={incentiveDetails.alert.title as string}
                bodyText={incentiveDetails.alert.body as string}
                iconKeyType={incentiveDetails.alert.icon as IconKeyType}
                alertType={incentiveDetails.alert.alertType}
              />
            </View>
          ) : null}

          <View style={styles.stepCheckListContainer}>
            <StepCheckListTracker
              stepCheckListData={stepCheckListData as StepChecklistProps}
              intervalTrackerData={intervalTrackerData}
            />
          </View>

          <Divider />

          <View style={styles.contentSectionContainer} testID="incentive-terms">
            <MultiStepTermsSection
              actions={incentiveDetails?.termsLinkAction}
              heading="Terms"
              html={incentiveDetails?.termsHtml}
              analyticsId="incentiveTermsClicked"
              customAttributes={{
                incentive_name: incentiveDetails?.incentiveTitle,
                incentive_variant:
                  incentiveDetails?.ctaButton?.buttonText ?? '',
                offer_id: incentiveDetails?.id,
              }}
              handlePdfClick={handlePdfClick}
            />
          </View>

          <Divider />

          {incentiveDetails?.ctaButton ? (
            <View
              style={
                isIOS
                  ? [styles.buttonContainer, styles.buttonBottomSpacing]
                  : styles.buttonContainer
              }
            >
              <Button
                label={incentiveDetails.ctaButton.buttonText}
                size="LARGE"
                type={incentiveDetails.ctaButton.buttonStyle}
                width="FULL"
                onPress={() => {
                  const link = (
                    incentiveDetails.ctaButton
                      ?.buttonOnClick as RouteNavigationAction
                  ).route;
                  router.push(link as Href);
                }}
              />
            </View>
          ) : null}
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  alertContainer: {
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.l,
    width: '100%',
  },
  bodyTextContainer: { textAlign: 'center' },
  buttonBottomSpacing: {
    paddingBottom: theme.spacing.xl,
  },
  buttonContainer: {
    backgroundColor: theme.color.background.primary,
    padding: theme.spacing.m,
    width: '100%',
  },
  container: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    display: 'flex',
    flex: 1,
    flexGrow: 1,
    justifyContent: 'center',
    minHeight: '100%',
    minWidth: '100%',
  },
  contentSectionContainer: {
    width: '100%',
  },
  dividerStyle: {
    backgroundColor: theme.color.background.surface,
    padding: theme.spacing.xs,
    width: '100%',
  },
  headerContainer: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.m,
    paddingBottom: theme.spacing.l,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.xxl,
    width: '100%',
  },
  loader: {
    alignItems: 'center',
    bottom: 0,
    height: '100%',
    justifyContent: 'center',
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
    width: '100%',
    zIndex: 1,
  },
  mainContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  offerDetailsHeaderContainer: {
    display: 'flex',
    flex: 1,
    flexGrow: 1,
    justifyContent: 'flex-start',
    width: '100%',
  },
  stepCheckListContainer: {
    paddingBottom: theme.spacing.l,
    width: '100%',
  },
});

export default MultiStepIncentiveDialog;
